import React, { useEffect, useState } from 'react';
import { post } from '../../libs/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/* ---------- Reusable validation helper ---------- */
const getRangeValidation = (value, min, max, label) => {
  if (value === '' || value === null || value === undefined) {
    return { isInvalid: false, message: '' };
  }

  const num = Number(value);
  if (num < min || num > max) {
    return {
      isInvalid: true,
      message: `${label} must be between ${min} and ${max}`,
    };
  }

  return { isInvalid: false, message: '' };
};

const Component = ({
  daysLeft,
  setDaysLeft,
  multiplier,
  setMultiplier,
  discountRate,
  setDiscountRate,
  downsideTrigger,
  setDownsideTrigger,
  calculationFrequency,
  setCalculationFrequency,
  setMaturityDate,
  endMatureDate,
  setIsFormValid,
}) => {
  const [calcDate, setCalcDate] = useState('');
  const [maturityError, setMaturityError] = useState('');

  /* ---------- Validations ---------- */
  const discountValidation = getRangeValidation(
    discountRate,
    0,
    3,
    'Discount Rate'
  );
  const downsideValidation = getRangeValidation(
    downsideTrigger,
    -5,
    -0.5,
    'Downside Trigger'
  );
  const multiplierValidation = getRangeValidation(
    multiplier,
    1,
    3,
    'Participation Multiplier'
  );

  useEffect(() => {
    const valid =
      !discountValidation.isInvalid &&
      !downsideValidation.isInvalid &&
      !multiplierValidation.isInvalid &&
      !(daysLeft > 0 && daysLeft < 30);

    setIsFormValid(valid);
  }, [
    discountValidation.isInvalid,
    downsideValidation.isInvalid,
    multiplierValidation.isInvalid,
    daysLeft,
    setIsFormValid,
  ]);

  const toDateOnly = (isoString) => {
    if (!isoString) return '';
    return isoString.split('T')[0];
  };

  // Format a Date to a local yyyy-MM-dd string (no UTC drift)
  const toLocalYmd = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse a yyyy-MM-dd string to a local Date (for react-datepicker `selected`)
  const parseLocalYmd = (str) => {
    if (!str) return null;
    const [year, month, day] = str.split('-');
    if (year && month && day) {
      const d = new Date(Number(year), Number(month) - 1, Number(day));
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  // function to calculate difference between today and selected date
  // `selected` is a Date object from react-datepicker (or null on clear)
  const handleDateSelect = async (selected) => {
    const selectedDate = toLocalYmd(selected);
    setCalcDate(selectedDate);
    setMaturityError('');

    if (!selectedDate) {
      setDaysLeft(0);
      setMaturityDate('');
      return;
    }

    const today = new Date();
    const picked = new Date(selectedDate);

    // difference in milliseconds
    const diffTime = picked - today;

    // convert to days
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      setDaysLeft(diffDays);

      let data;
      try {
        data = await post('/investment/maturity_date', {
          productType: 'SSPS',
          calculationPeriod: diffDays,
        });
      } catch (err) {
        console.error('maturity_date request failed:', err);
      }

      const parsed = data && data.maturityDate ? new Date(data.maturityDate) : null;
      // Treat a missing/empty/unparseable/past-year response as a hard failure
      // and surface it, instead of silently leaving maturityDate empty — which
      // otherwise only shows up as a confusing guard error at submit. (PB-1697)
      if (
        !data ||
        !data.maturityDate ||
        isNaN(parsed.getTime()) ||
        parsed.getUTCFullYear() < new Date().getUTCFullYear()
      ) {
        console.error(
          'Invalid maturityDate from /investment/maturity_date:',
          data && data.maturityDate
        );
        setMaturityDate('');
        setCalcDate('');
        setDaysLeft(0);
        setMaturityError(
          'Could not calculate maturity date — please retry or contact support.'
        );
        return;
      }
      setMaturityDate(data.maturityDate);
    }
  };

  // Seed maturity from the pre-filled default. react-datepicker does not fire
  // onChange for the value shown via `selected`, so without this the displayed
  // default date is cosmetic: maturityDate stays empty and the order is blocked
  // at submit with a confusing "set a valid maturity date" error even though a
  // date is visibly shown. Applying the default here mirrors a manual pick.
  // (PB-1697)
  useEffect(() => {
    if (calcDate || maturityError) return; // user already picked / error surfaced
    const defaultDate = parseLocalYmd(
      endMatureDate ? toDateOnly(endMatureDate) : ''
    );
    if (defaultDate) {
      handleDateSelect(defaultDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMatureDate]);

  // min date = tomorrow
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 30); // min 30 days

  // max date = 365 days from now
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 365);

  return (
    <div className="p-3">
      <div className="card-text">
        <div className="row g-3">
          {/* Calculation Period */}
          <div className="col-md-12">
            <div className="d-flex align-items-center">
              <span className="col-6 sub-text">Total Fixing Days</span>
              <DatePicker
                className="form-control col-6"
                dateFormat="yyyy-MM-dd"
                placeholderText="yyyy-MM-dd"
                selected={
                  maturityError
                    ? null
                    : parseLocalYmd(
                        calcDate ||
                          (endMatureDate ? toDateOnly(endMatureDate) : '')
                      )
                }
                minDate={minDate}
                maxDate={maxDate}
                showYearDropdown
                dropdownMode="select"
                onChange={(date) => handleDateSelect(date)}
              />
            </div>

            {daysLeft > 0 && daysLeft < 30 && (
              <small className="text-danger ms-2">
                Calculation period must be at least 30 days
              </small>
            )}

            {maturityError && (
              <small className="text-danger ms-2 d-block">
                {maturityError}
              </small>
            )}
          </div>

          {/* Discount Rate */}
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <span className="col-8 sub-text">Price Discount Factor</span>
              <input
                type="number"
                min={0}
                max={3}
                step="0.01"
                className={`form-control col-4 ${
                  discountValidation.isInvalid ? 'is-invalid' : ''
                }`}
                value={discountRate ?? ''}
                onChange={(e) => setDiscountRate(e.target.value)}
              />
            </div>

            {discountValidation.isInvalid && (
              <small className="text-danger ms-2">
                {discountValidation.message}
              </small>
            )}
          </div>

          <p>Normal-day participation is fixed at 1x.</p>

          {/* Boost Factor 2X */}
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <span className="col-8 sub-text">
                Participation Scaling Factor
              </span>
              <input
                type="number"
                min={1}
                max={3}
                step="0.01"
                className={`form-control col-4 ${
                  multiplierValidation.isInvalid ? 'is-invalid' : ''
                }`}
                value={multiplier ?? ''}
                onChange={(e) => setMultiplier(e.target.value)}
              />
            </div>
            {multiplierValidation.isInvalid && (
              <small className="text-danger ms-2">
                {multiplierValidation.message}
              </small>
            )}
          </div>

          {/* Downside Trigger */}
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <span className="col-8 sub-text">
                Underlying Performance Trigger
              </span>
              <input
                type="number"
                min={-5}
                max={-0.5}
                step="0.1"
                className={`form-control col-4 ${
                  downsideValidation.isInvalid ? 'is-invalid' : ''
                }`}
                value={downsideTrigger ?? ''}
                onChange={(e) => setDownsideTrigger(e.target.value)}
              />
            </div>

            {downsideValidation.isInvalid && (
              <small className="text-danger ms-2">
                {downsideValidation.message}
              </small>
            )}
          </div>

          {/* Calculation Frequency */}
          <div className="col-md-6 d-flex align-items-center">
            <span className="col-8 sub-text">Fixing Frequency (Day)</span>
            {'Daily'}
            {/* <input
              type="number"
              className="form-control col-4"
              value={calculationFrequency ?? ''}
              onChange={(e) => setCalculationFrequency(e.target.value)}
            /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Component;
